import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { verifyMailbox } from "../graphService";

/**
 * Adds a mailbox directly (when consent is already granted)
 */
export async function addMailbox(email: string, tenantId: string | null): Promise<boolean> {
  if (!tenantId) {
    console.error("No tenant ID provided");
    toast.error("Authentication error");
    return false;
  }

  try {
    // Verify the mailbox exists in Microsoft 365 and get its details
    const mailboxInfo = await verifyMailbox(email);
    
    if (!mailboxInfo) {
      toast.error("Failed to verify mailbox");
      return false;
    }
    
    // Add the mailbox with the verified information
    const mailboxData = {
      tenant_id: tenantId,
      email: mailboxInfo.email,
      name: mailboxInfo.name,
      type: mailboxInfo.type,
      status: 'active',
      graph_id: mailboxInfo.id
    };
    
    const { error } = await supabase.from('mailboxes').insert(mailboxData);
    
    if (error) {
      if (error.code === '23505') { // Unique constraint violation
        toast.error("This mailbox is already connected to your account");
      } else {
        throw error;
      }
      return false;
    }
    
    toast.success("Mailbox added successfully");
    return true;
  } catch (error: unknown) {
    let errMessage = "Failed to add mailbox";
    if (error instanceof Error) {
        errMessage = `${errMessage}. (${error.message})`;
        console.error("Error adding mailbox:", error.message);
    }
    console.error(errMessage, error);
    toast.error(errMessage);
    return false;
  }
}

/**
 * Handles the consent callback and adds the mailbox
 */
export async function handleConsentCallback(code: string, email: string, tenantId: string | null): Promise<boolean> {
  if (!tenantId) {
    console.error("No tenant ID provided");
    toast.error("Authentication error");
    return false;
  }

  try {
    // With our new approach, the code is exchanged for a token in the backend
    // For the MVP, we'll simply verify the mailbox with our edge function
    const mailboxInfo = await verifyMailbox(email);
    
    if (!mailboxInfo) {
      toast.error("Failed to verify mailbox");
      return false;
    }
    
    // Add the mailbox with the verified information
    const mailboxData = {
      tenant_id: tenantId,
      email: mailboxInfo.email,
      name: mailboxInfo.name,
      type: mailboxInfo.type,
      status: 'active',
      graph_id: mailboxInfo.id
    };
    
    const { error, data } = await supabase.from('mailboxes').insert(mailboxData);
    
    if (error) {
      const errorResponse = data as { error: string; details: { error: { code?: string; message?: string } } };
      const mainError = errorResponse?.error || "Unknown error";
      const errorDetails = errorResponse?.details?.error;
      const errorMessage = errorDetails?.message || "Unknown error occurred";
      const errorCode = errorDetails?.code || "No error code";

      console.error("Error adding mailbox:", mainError, errorMessage, "Code:", errorCode);
      toast.error(`Failed to add mailbox: ${mainError} - ${errorMessage} (Code: ${errorCode})`);
      return false;
    }
    
    toast.success("Mailbox added successfully");
    return true;
  } catch (error: unknown) {
    if (error instanceof Error) {
        console.error("Error adding mailbox:", error.message);
    } else {
        console.error("Error adding mailbox:", error);
    }
    toast.error("Failed to add mailbox");
    return false;
  }
}
